import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, ArrowLeft, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "@/lib/adminApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_66258ad9-e000-456c-bbb1-9b3da697cdba/artifacts/pl46n86d_New%20SCAH%20Logo.png";

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof detail === "object" && detail.msg) return detail.msg;
  return String(detail);
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing or invalid reset link. Please request a new one.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, {
        token,
        new_password: password,
      });
      toast.success("Password updated. You are now signed in.");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(formatDetail(error.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-900 flex items-center justify-center p-6"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="SCAH Logo" className="h-20 w-auto mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold text-white mb-2">Choose a new password</h1>
          <p className="text-slate-400">Sickle Cell Association</p>
        </div>

        <Card className="border-slate-800 bg-slate-800/50">
          <CardContent className="p-8">
            {!token ? (
              <div className="text-center" data-testid="reset-invalid-token">
                <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto mb-4" />
                <h2 className="text-white text-xl font-semibold mb-2">Invalid reset link</h2>
                <p className="text-slate-400 text-sm">
                  This password reset link is missing or malformed. Please request a new one.
                </p>
                <Link
                  to="/admin/forgot-password"
                  className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm mt-6"
                  data-testid="request-new-link"
                >
                  Request a new link <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <p className="text-slate-400 text-sm mb-6">
                  Enter a new password for your admin account. Minimum 8 characters.
                </p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-password" className="text-slate-300">
                      New Password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        minLength={8}
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm-password" className="text-slate-300">
                      Confirm Password
                    </Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        minLength={8}
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 mt-2"
                    data-testid="reset-submit-btn"
                  >
                    {loading ? "Updating password..." : "Update Password"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  <div className="text-center pt-2">
                    <Link
                      to="/admin/login"
                      className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm"
                      data-testid="back-to-login-link"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back to login
                    </Link>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          <a href="/" className="hover:text-slate-300">
            ← Back to Website
          </a>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
